// Pure helpers for the equestrian show record + credentials (Tier 3 SP4).
//
// The show record is DERIVED from logged result rows, never stored — a mirror
// of the PR/SB derivation on event marks. NCEA is head-to-head win/loss by
// event; IHSA accumulates placement points by ladder toward a qualifying tier.
// This is a JS port of backend/app/services/equestrian_scoring.py — keep the
// point scale and `summarize` output shape in lockstep. The API already returns
// `summary` for display; `summarize` here backs the athlete editor's live
// preview before a save round-trips.

// Canonical IHSA placement → points scale (1st..6th). Must match the backend.
export const IHSA_POINTS = { 1: 7, 2: 5, 3: 4, 4: 3, 5: 2, 6: 1 };

// Qualifying-tier ordering so "highest reached" is well-defined.
const TIER_RANK = { '': 0, regional: 1, zone: 2, national: 3 };
const tierRank = (t) => TIER_RANK[t || ''] || 0;

// Explicit points override wins; else map a 1-6 placement; else null.
export function ihsaPointsFor(placement, pointsOverride) {
  if (pointsOverride != null) return pointsOverride;
  if (placement != null && Object.prototype.hasOwnProperty.call(IHSA_POINTS, placement)) {
    return IHSA_POINTS[placement];
  }
  return null;
}

// JS port of the backend `summarize`. Returns
// { ncea: { event: { wins, losses } },
//   ihsa: { ladder: { seasonYearStr: { points, qualified_for } } } }.
export function summarize(rows) {
  const ncea = {};
  const ihsa = {};
  for (const r of rows || []) {
    const fmt = r.format;
    if (fmt === 'ncea') {
      const ev = r.event;
      if (!ev) continue;
      if (!ncea[ev]) ncea[ev] = { wins: 0, losses: 0 };
      if (r.result === 'win') ncea[ev].wins += 1;
      else if (r.result === 'loss') ncea[ev].losses += 1;
    } else if (fmt === 'ihsa') {
      const ladder = r.ladder;
      if (!ladder) continue;
      const year = String(r.season_year);
      if (!ihsa[ladder]) ihsa[ladder] = {};
      if (!ihsa[ladder][year]) ihsa[ladder][year] = { points: 0, qualified_for: null };
      const bucket = ihsa[ladder][year];
      const pts = ihsaPointsFor(r.placement, r.points);
      if (pts) bucket.points += pts;
      const tier = r.qualified_for;
      if (tierRank(tier) > tierRank(bucket.qualified_for)) bucket.qualified_for = tier;
    }
  }
  return { ncea, ihsa };
}

// ── Label maps ────────────────────────────────────────────────────────────────
export const CREDENTIAL_TYPE_LABEL = {
  membership: 'Membership',
  safety: 'Safety',
  eligibility: 'Eligibility',
  rating: 'Rating',
};

export const FORMAT_LABEL = {
  ncea: 'NCEA',
  ihsa: 'IHSA',
};

export const NCEA_EVENT_LABEL = {
  flat: 'Flat',
  fences: 'Fences',
  horsemanship: 'Horsemanship',
  reining: 'Reining',
};

export const LADDER_LABEL = {
  hunt_seat: 'Hunt Seat',
  western: 'Western',
};

export const RESULT_LABEL = {
  win: 'Win',
  loss: 'Loss',
};

export const QUALIFIED_FOR_LABEL = {
  regional: 'Regional',
  zone: 'Zone',
  national: 'National',
};

// Ordered option lists for picklists (value/label pairs).
export const CREDENTIAL_TYPE_OPTIONS = [
  { value: 'membership', label: 'Membership' },
  { value: 'safety', label: 'Safety' },
  { value: 'eligibility', label: 'Eligibility' },
  { value: 'rating', label: 'Rating' },
];

export const QUALIFIED_FOR_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'regional', label: 'Regional' },
  { value: 'zone', label: 'Zone' },
  { value: 'national', label: 'National' },
];
